// Start/end dropdowns in 15-minute steps (SPEC §9.4). The end follows the start keeping the current length; a
// hand-set end changes that length (SPEC §12 scenario 25). The length is shown next to them.

import { CELLS_PER_DAY, followStart, formatCell, rangeMinutes, type CellRange } from "../time";
import { formatDuration, t } from "../i18n/i18n";
import { createElement } from "./dom";
import { createSelect, type SelectOption } from "./forms";

export interface TimeRangePicker {
  element: HTMLElement;
  getRange: () => CellRange;
}

/** A picker starting at the given range. */
export function createTimeRangePicker(initial: CellRange): TimeRangePicker {
  let range: CellRange = initial;
  const startSelect: HTMLSelectElement = createSelect(cellOptions(0, CELLS_PER_DAY - 1), String(range.start));
  const endSelect: HTMLSelectElement = createSelect(cellOptions(1, CELLS_PER_DAY), String(range.end));
  const lengthLabel: HTMLSpanElement = createElement("span", "time-length", formatDuration(rangeMinutes(range)));
  startSelect.setAttribute("aria-label", t("app.start"));
  endSelect.setAttribute("aria-label", t("app.end"));
  startSelect.addEventListener("change", (): void => {
    range = followStart(range, Number(startSelect.value));
    showRange(range);
  });
  endSelect.addEventListener("change", (): void => {
    range = { start: range.start, end: Math.max(Number(endSelect.value), range.start + 1) };
    showRange(range);
  });

  function showRange(shown: CellRange): void {
    startSelect.value = String(shown.start);
    endSelect.value = String(shown.end);
    lengthLabel.textContent = formatDuration(rangeMinutes(shown));
  }

  const element: HTMLSpanElement = createElement("span", "time-range-picker");
  element.append(startSelect, createElement("span", "", "–"), endSelect, lengthLabel);
  return { element, getRange: (): CellRange => range };
}

function cellOptions(firstCell: number, lastCell: number): SelectOption[] {
  const options: SelectOption[] = [];
  for (let cell = firstCell; cell <= lastCell; cell++) {
    options.push({ value: String(cell), label: formatCell(cell) });
  }

  return options;
}
